import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  register as registerSW,
  requestNotificationPermission,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  requestBackgroundSync,
  addServiceWorkerMessageListener
} from '../utils/serviceWorker';

interface PWAContextType {
  // Installation
  isInstallable: boolean;
  isInstalled: boolean;
  installApp: () => Promise<void>;
  
  // Network status
  isOnline: boolean;
  
  // Service worker
  isServiceWorkerReady: boolean;
  hasServiceWorkerUpdate: boolean;
  updateServiceWorker: () => void;
  
  // Notifications
  notificationPermission: NotificationPermission;
  requestNotifications: () => Promise<NotificationPermission>;
  isSubscribedToPush: boolean;
  subscribeToPush: () => Promise<boolean>;
  unsubscribeFromPush: () => Promise<boolean>;
  
  // Background sync
  requestSync: (tag: string) => Promise<void>;
  
  // Offline status
  showOfflineMessage: boolean;
  dismissOfflineMessage: () => void;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

interface PWAProviderProps {
  children: ReactNode;
}

export function PWAProvider({ children }: PWAProviderProps) {
  // Installation state
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  
  // Network state - always online (offline functionality disabled)
  const [isOnlineState] = useState(true);
  
  // Service worker state
  const [isServiceWorkerReady, setIsServiceWorkerReady] = useState(false);
  const [hasServiceWorkerUpdate, setHasServiceWorkerUpdate] = useState(false);
  const [serviceWorkerRegistration, setServiceWorkerRegistration] = useState<ServiceWorkerRegistration | null>(null);
  
  // Notification state
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied'
  );
  const [isSubscribedToPush, setIsSubscribedToPush] = useState(false);
  
  // Offline message state - always hidden (offline functionality disabled)
  const [showOfflineMessage] = useState(false);

  // Initialize PWA features
  useEffect(() => {
    // Register service worker
    registerSW({
      onSuccess: (registration) => {
        console.log('PWA: Service worker registered successfully');
        setIsServiceWorkerReady(true);
        setServiceWorkerRegistration(registration);
        checkPushSubscription(registration);
      },
      onUpdate: (registration) => {
        console.log('PWA: Service worker update available');
        setHasServiceWorkerUpdate(true);
        setServiceWorkerRegistration(registration);
      },
      onOfflineReady: () => {
        console.log('PWA: App is ready for offline use');
        setIsServiceWorkerReady(true);
      }
    });

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if app is already installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    // Check if running in standalone mode (already installed)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Network status monitoring - disabled (offline functionality disabled)
  // useEffect(() => {
  //   // Network monitoring disabled
  // }, []);

  // Service worker message listener
  useEffect(() => {
    const removeMessageListener = addServiceWorkerMessageListener((message) => {
      console.log('PWA: Service worker message received', message);
      
      if (message.type === 'SYNC_DRAFTS') {
        // Notify components to sync their drafts
        window.dispatchEvent(new CustomEvent('pwa-sync-drafts', { detail: message }));
      }
    });

    return removeMessageListener;
  }, []);

  // Check push subscription status
  const checkPushSubscription = async (registration: ServiceWorkerRegistration) => {
    try {
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribedToPush(!!subscription);
    } catch (error) {
      console.error('PWA: Failed to check push subscription', error);
    }
  };

  // Install app
  const installApp = async (): Promise<void> => {
    if (!deferredPrompt) {
      throw new Error('App installation not available');
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('PWA: User accepted the install prompt');
        setIsInstalled(true);
      } else {
        console.log('PWA: User dismissed the install prompt');
      }
      
      setDeferredPrompt(null);
      setIsInstallable(false);
    } catch (error) {
      console.error('PWA: Failed to install app', error);
      throw error;
    }
  };

  // Update service worker
  const updateServiceWorker = () => {
    if (serviceWorkerRegistration?.waiting) {
      serviceWorkerRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };

  // Request notification permission
  const requestNotifications = async (): Promise<NotificationPermission> => {
    try {
      const permission = await requestNotificationPermission();
      setNotificationPermission(permission);
      return permission;
    } catch (error) {
      console.error('PWA: Failed to request notification permission', error);
      return 'denied';
    }
  };

  // Subscribe to push notifications
  const subscribeToPush = async (): Promise<boolean> => {
    try {
      const subscription = await subscribeToPushNotifications();
      const success = !!subscription;
      setIsSubscribedToPush(success);
      
      if (success && subscription) {
        // Send subscription to server
        // This would typically be done via your API
        console.log('PWA: Push subscription created', subscription);
      }
      
      return success;
    } catch (error) {
      console.error('PWA: Failed to subscribe to push notifications', error);
      return false;
    }
  };

  // Unsubscribe from push notifications
  const unsubscribeFromPush = async (): Promise<boolean> => {
    try {
      const success = await unsubscribeFromPushNotifications();
      setIsSubscribedToPush(!success);
      return success;
    } catch (error) {
      console.error('PWA: Failed to unsubscribe from push notifications', error);
      return false;
    }
  };

  // Request background sync
  const requestSync = async (tag: string): Promise<void> => {
    try {
      await requestBackgroundSync(tag);
    } catch (error) {
      console.error('PWA: Failed to request background sync', error);
      throw error;
    }
  };

  // Dismiss offline message - no-op (offline functionality disabled)
  const dismissOfflineMessage = () => {
    // No-op
  };

  const contextValue: PWAContextType = {
    // Installation
    isInstallable,
    isInstalled,
    installApp,
    
    // Network status
    isOnline: isOnlineState,
    
    // Service worker
    isServiceWorkerReady,
    hasServiceWorkerUpdate,
    updateServiceWorker,
    
    // Notifications
    notificationPermission,
    requestNotifications,
    isSubscribedToPush,
    subscribeToPush,
    unsubscribeFromPush,
    
    // Background sync
    requestSync,
    
    // Offline status
    showOfflineMessage,
    dismissOfflineMessage
  };

  return (
    <PWAContext.Provider value={contextValue}>
      {children}
    </PWAContext.Provider>
  );
}

export function usePWA(): PWAContextType {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
}

export default PWAContext;